import {
  transAbbreviation,
  transRegion as baseTransRegion,
  initialUpperCase,
} from '@/pages/opinion/utils';
import moment from 'moment';
import { channelInfo } from '../../ChannelLabel';
import {
  brandHealthFormat,
  sentimentFormat,
} from '@op/utils/value';
import {
  IIntentOptions,
  transIntent,
} from '@op/pages/report/VoiceReportingTextAnalysis';
import { getMonitoringSource } from '@op/components/TableCommonHook/utils';

export function defaultTo(to = '-') {
  return (item: { [x: string]: any }, key: string | number) => {
    const value = item[key];
    if (!value) {
      return to;
    }
    return item[key];
  };
}

export function brandHealthRenderHelper(
  item: { [x: string]: string | number },
  key: string | number,
) {
  return brandHealthFormat(item[key]);
}

export function reviewerPlaytime(
  item: {
    record_duration: any;
    review_duration: any;
    channel_name: any;
  },
  key: string | number,
) {
  const { record_duration, review_duration, channel_name } = item;

  if (channel_name !== 'steam') return '';

  const hasDuration = (num: number) => {
    if (!isNaN(num) && num > 0) {
      return true;
    }
    return false;
  };

  const hasRecordDuration = hasDuration(record_duration);
  const hasReviewDuration = hasDuration(review_duration);
  if (key === 'on_record_time' && hasRecordDuration) {
    return record_duration;
  }
  if (key === 'at_review_time' && hasReviewDuration) {
    return review_duration;
  }

  return '-';
}

export function gameStoreRating(
  item: {
    [x: string]: any;
    channel_name?: any;
    is_recommend?: any;
    monitor_source?: any;
  },
  key: string | number,
) {
  const { channel_name, is_recommend, monitor_source } = item;

  if (channel_name === 'steam') {
    if (is_recommend === 1) return 'Recommended';
    if (is_recommend === 0) return 'Not Recommended';
  }

  if (channel_name === 'metacritic') {
    let reviews = '';
    if (typeof monitor_source === 'string') {
      reviews = monitor_source?.toLowerCase?.()?.trim();
    } else if (Array.isArray(monitor_source)) {
      reviews = monitor_source[0].type?.toLowerCase?.()?.trim();
    }

    const review = reviews.includes('_') ? reviews.split('_')[0] : '';

    let score = item[key];
    if (score == -1) return 'Unscored';

    score = isNaN(item[key]) ? 0 : item[key];

    if ('user' == review) {
      return `User Score：${Number(score).toFixed(1)}`;
    }
    if ('critic' == review) {
      return `Metascore：${Number(score).toFixed(0)}`;
    }
  }

  let score = item[key];
  if (score == -1) return 'Unscored';

  return tryTransNumber(item, key);
}

const sentimentRatingMap: any = {
  1: 'Negative',
  3: 'Neutral',
  5: 'Positive',
};

const importanceEditingMap: any = {
  2: 'High',
  1: 'Medium',
  3: 'Low',
  0: 'Invalid',
};

export function hideSentimentFunc(
  item: { [x: string]: any },
  key: string | number,
) {
  const value = item[key];
  return sentimentRatingMap[value] || 'Untagged';
}

export function sentimentNumber(
  item: { [x: string]: any },
  key: string | number,
) {
  const realValue = item[key];
  if (!realValue || realValue === '0' || Number(realValue) === 0) {
    return '-';
  }
  if (!isNaN(realValue)) {
    const newValue = Number(sentimentFormat(realValue));
    return isNaN(newValue) ? '-' : newValue;
  }
  return realValue;
}

export function renderValidation(
  item: { [x: string]: any },
  key: string | number,
) {
  const element = item?.[key];
  return importanceEditingMap[element];
}

// 转成百分比的数字类型，并且保留指定的小数
// 比如 96，转成 0.96
export function toPercentNumber(hasHundred = true, digits = 4) {
  return function (item: { [x: string]: any }, key: string | number) {
    if (!isNaN(item[key])) {
      return Number((item[key] / (hasHundred ? 100 : 1)).toFixed(digits));
    }
    return item[key];
  };
}

export function toPercent(digits = 2) {
  return function (item: { [x: string]: any }, key: string | number) {
    if (!isNaN(item[key])) {
      return `${(item[key] * 100).toFixed(digits)}%`;
    }
    return item[key];
  };
}

export function tryToFixed(digits = 2) {
  return function (item: { [x: string]: any }, key: string | number) {
    if (!item[key] || item[key] === '0' || Number(item[key]) === 0) {
      return '-';
    }

    if (!isNaN(item[key])) {
      return Number(item[key]).toFixed(digits);
    }
    return item[key];
  };
}

export function tryTransNumber(
  item: { [x: string]: any },
  key: string | number,
) {
  if (!isNaN(item[key])) {
    return Number(item[key]);
  }
  return item[key];
}

export function tryTransNumberDefaultLine(
  item: { [x: string]: any },
  key: string | number,
) {
  if (!item[key] || item[key] === '0' || Number(item[key]) === 0) {
    return '-';
  }
  if (!isNaN(item[key])) {
    return Number(item[key]);
  }
  return item[key];
}

export function commentType(item: {
  comment_id: string;
  comment_parent_id: string;
}) {
  return item.comment_id !== '-1' && item.comment_parent_id === '-1'
    ? 'Posts'
    : 'Reply';
}

export function monitorSourceName(item: {
  keyword_or_account: any;
  monitor_source: any;
  keyword_account_name: any;
}) {
  const { keyword_or_account, monitor_source, keyword_account_name } = item;
  // 老版本使用这个，新版本会是数组
  if (typeof monitor_source === 'string') {
    let sourceName = 'by Account:';
    if (keyword_or_account === 'keyword') {
      sourceName = 'by Keyword:';
      sourceName += keyword_account_name;
    } else if (monitor_source.length > 0) {
      sourceName += `${keyword_account_name}(${monitor_source})`;
    } else {
      sourceName += keyword_account_name;
    }
    return sourceName;
  }

  if (Array.isArray(monitor_source)) {
    const { keyword, external, accountCategories } =
      getMonitoringSource(monitor_source);
    const parts: string[] = [];

    // Keyword
    if (keyword.length > 0) {
      parts.push(`by Keyword: ${keyword.map((item) => item.name).join(', ')}`);
    }

    // Account Categories
    accountCategories.forEach((category) => {
      if (category.list.length > 0) {
        parts.push(
          `${category.textInfo.text}: ${category.list
            .map((item) => item.name)
            .join(', ')}`,
        );
      }
    });

    // External
    if (external.length > 0) {
      parts.push(
        `by External Data Sources: ${external
          .map((item) => item.name)
          .join(', ')}`,
      );
    }

    return `${parts.join('; ')} `;
  }

  return '';
}

export function formatTime(format = 'YYYY-MM-DD HH:mm:ss') {
  return (item: { [key: string]: any }, key: string) =>
    moment(item[key]).format(format);
}

export function transRegion(type = 3) {
  return (item: { [key: string]: any }, key: string) =>
    baseTransRegion(item[key], type);
}

export function biggerOrTo(base: number, to: string | number = '') {
  return (item: { [key: string]: any }, key: string) => {
    const value = item[key];
    if (!(value > base)) {
      return to;
    }
    return item[key];
  };
}

export function smallerOrTo(base: number, to: string | number = '') {
  return (item: { [key: string]: any }, key: string) => {
    const value = item[key];
    if (!(value < base)) {
      return to;
    }
    return item[key];
  };
}

// 内容回退兼容处理
export function bakContent(bak: string) {
  return (item: { [key: string]: any }, key: string) =>
    item[key] || item?.[bak]?.[key];
}

// 处理语言
export function dealwithLanguage(
  item: { [x: string]: string },
  key: string | number,
) {
  // console.log(item, key);

  return transAbbreviation(item[key], 'lang_en');
}

export function dealwithCoutry(
  item: { [x: string]: string },
  key: string | number,
) {
  return transAbbreviation(item[key], 'region_en');
}

// 处理渠道;
export function dealwithChannel(item: any, key: string | number) {
  const { name } = channelInfo(item[key]);
  return name;
}

// 处理网红;
export function translateInfluencer(
  item: { [x: string]: string },
  key: string | number,
) {
  const map: any = {
    common: 'Common(0K ~ 1K)',
    nano: 'Nano(1K ~ 10K)',
    micro: 'Micro(10k ~ 50k)',
    'mid-tier': 'Mid-Tier(50k ~ 500k)',
    macro: 'Macro(500k ~ 1M)',
    mega: 'Mega(1M+)',
  };
  const value = item?.[key];
  return initialUpperCase(map[value] || value);
}

export function dealwithAccountChannelInfo(
  type: string,
  params: { split_text: any },
  isAccount: boolean,
) {
  return (item: { [x: string]: any }, key: string | number) => {
    const channel = item[key];
    const splitText = params.split_text;
    if (isAccount) {
      if (channel?.toLocaleLowerCase() !== 'others') {
        const [_newChannel, newName] = channel?.split(splitText) || [];
        const { name } = channelInfo(_newChannel);
        const finalName = newName.replace(_newChannel, name);
        return type === 'name' ? finalName : name;
      }
      return 'Others';
    }
    const { name } = channelInfo(channel);
    return name;
  };
}

export function equalAndTo(base: string, to = '') {
  return (item: { [x: string]: any }, key: string | number) => {
    const value = item[key];

    if (value === base) {
      return to;
    }
    return item[key];
  };
}

export function transIntentRenderHelper(intentOptions: IIntentOptions[]) {
  return (item: { [key: string]: any }, key: string) =>
    transIntent(intentOptions, item[key]);
}

export function renderHelperViewImpairmentRate(
  item: { [x: string]: number },
  key: string | number,
) {
  if (item?.[key] === -99999) {
    return '-';
  }
  return item[key];
}

export function renderHelperLiveBroadcastViews(
  item: {
    [x: string]: number | string;
  },
  key: string | number,
) {
  if (item?.channel_name !== 'chzzk') return '-';
  try {
    const obj = JSON.parse(item?.ext_json);
    const value = obj[key];
    return value || '-';
  } catch (error) {
    return '-';
  }
}
